import { useEffect, useState } from "react";

// @mui
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Icon from "@mui/material/Icon";

// MD components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

// Layout
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

import { apiFetch } from "utils/api";

function toDeDateTime(value) {
  if (!value) return "-";
  try {
    return new Date(value).toLocaleString("de-DE");
  } catch {
    return "-";
  }
}

function toDateInputValue(value) {
  if (!value) return "";
  try {
    const d = new Date(value);
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  } catch {
    return "";
  }
}

function getApiBaseUrl() {
  // In production we MUST use same-origin (prevents Mixed Content + blocked HTTP requests)
  // We standardize on a base WITHOUT the trailing `/api` so we can append API paths consistently.
  // Optional override for dev:
  //   REACT_APP_SOLVIT_API_BASE=https://loopbox.cloud/solvit-api   (preferred)
  //   REACT_APP_SOLVIT_API_BASE=https://loopbox.cloud/solvit-api/api (also supported)
  const env = process.env.REACT_APP_SOLVIT_API_BASE;
  const fallback = `${window.location.origin}/solvit-api`;

  const rawBase = env && env.trim() ? env.trim().replace(/\/$/, "") : fallback;

  // Allow env to be provided with or without `/api`
  return rawBase.replace(/\/api$/i, "");
}

function buildDownloadUrl(url) {
  if (!url) return "";

  // If backend already returns absolute URL
  if (/^https?:\/\//i.test(url)) return url;

  const normalized = url.startsWith("/") ? url : `/${url}`;

  // If it already contains the nginx prefix
  if (normalized.startsWith("/solvit-api/")) {
    return `${window.location.origin}${normalized}`;
  }

  // Otherwise assume it is an uploads path like `/uploads/...`
  return `${window.location.origin}/solvit-api${normalized}`;
}

export default function AdminInvoices() {
  const [customers, setCustomers] = useState([]);
  const [customerId, setCustomerId] = useState("");
  const [invoices, setInvoices] = useState([]);

  // Edit dialog
  const [editOpen, setEditOpen] = useState(false);
  const [editInv, setEditInv] = useState(null);
  const [editMonth, setEditMonth] = useState("");
  const [editAmount, setEditAmount] = useState("");
  const [editStatus, setEditStatus] = useState("Offen");
  const [editIssuedAt, setEditIssuedAt] = useState("");
  const [savingEdit, setSavingEdit] = useState(false);
  const [deletingId, setDeletingId] = useState("");

  const [loadingCustomers, setLoadingCustomers] = useState(true);
  const [loadingInvoices, setLoadingInvoices] = useState(false);
  const [error, setError] = useState("");

  // Upload form
  const [month, setMonth] = useState("");
  const [amount, setAmount] = useState("");
  const [status, setStatus] = useState("Offen");
  const [issuedAt, setIssuedAt] = useState("");
  const [pdfFile, setPdfFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadMsg, setUploadMsg] = useState("");

  useEffect(() => {
    const run = async () => {
      setLoadingCustomers(true);
      setError("");
      try {
        const data = await apiFetch("/api/admin/customers");
        setCustomers(Array.isArray(data) ? data : data?.customers || []);
      } catch (e) {
        setCustomers([]);
        setError(e?.message || "Kunden konnten nicht geladen werden.");
      } finally {
        setLoadingCustomers(false);
      }
    };
    run();
  }, []);

  const loadInvoices = async (cid) => {
    if (!cid) {
      setInvoices([]);
      return;
    }

    setLoadingInvoices(true);
    setError("");
    try {
      let data;
      try {
        data = await apiFetch(`/api/admin/invoices?customerId=${encodeURIComponent(cid)}`);
      } catch (err) {
        if (String(err?.message || "").includes("404")) {
          data = await apiFetch(`/api/invoices?customerId=${encodeURIComponent(cid)}`);
        } else {
          throw err;
        }
      }

      setInvoices(Array.isArray(data) ? data : data?.invoices || []);
    } catch (e) {
      setInvoices([]);
      setError(e?.message || "Rechnungen konnten nicht geladen werden.");
    } finally {
      setLoadingInvoices(false);
    }
  };

  useEffect(() => {
    loadInvoices(customerId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId]);

  const openEdit = (inv) => {
    setEditInv(inv);
    setEditMonth(inv?.month || "");
    setEditAmount(inv?.amount || "");
    setEditStatus(inv?.status || "Offen");
    setEditIssuedAt(toDateInputValue(inv?.issuedAt));
    setEditOpen(true);
  };

  const closeEdit = () => {
    setEditOpen(false);
    setEditInv(null);
  };

  const saveEdit = async () => {
    if (!editInv?.id) return;
    if (!editMonth.trim()) return setError("Bitte Monat angeben (z.B. 2025-12).");

    setSavingEdit(true);
    setError("");
    try {
      const body = {
        month: editMonth.trim(),
        amount: editAmount || null,
        status: editStatus || "Offen",
        issuedAt: editIssuedAt ? new Date(`${editIssuedAt}T00:00:00.000Z`).toISOString() : null,
      };

      await apiFetch(`/api/admin/invoices/${encodeURIComponent(editInv.id)}`, {
        method: "PUT",
        body,
      });

      closeEdit();
      await loadInvoices(customerId);
    } catch (e) {
      setError(e?.message || "Speichern fehlgeschlagen.");
    } finally {
      setSavingEdit(false);
    }
  };

  const deleteInvoice = async (inv) => {
    if (!inv?.id) return;
    const ok = window.confirm(`Rechnung ${inv.month || inv.id} wirklich löschen?`);
    if (!ok) return;

    setDeletingId(inv.id);
    setError("");
    try {
      await apiFetch(`/api/admin/invoices/${encodeURIComponent(inv.id)}`, {
        method: "DELETE",
      });
      await loadInvoices(customerId);
    } catch (e) {
      setError(e?.message || "Löschen fehlgeschlagen.");
    } finally {
      setDeletingId("");
    }
  };

  const onUpload = async (e) => {
    e.preventDefault();
    setUploadMsg("");

    if (!customerId) return setUploadMsg("Bitte zuerst einen Kunden auswählen.");
    if (!month.trim()) return setUploadMsg("Bitte Monat angeben (z.B. 2025-12).");
    if (!pdfFile) return setUploadMsg("Bitte eine PDF auswählen.");

    const fd = new FormData();
    fd.append("customerId", customerId);
    fd.append("month", month.trim());
    if (amount) fd.append("amount", amount);
    fd.append("status", status || "Offen");
    if (issuedAt) {
      // issuedAt from <input type="date"> is YYYY-MM-DD; normalize to ISO safely
      const iso = new Date(`${issuedAt}T00:00:00.000Z`).toISOString();
      fd.append("issuedAt", iso);
    }
    fd.append("pdf", pdfFile);

    setUploading(true);
    setError("");
    try {
      const token = localStorage.getItem("solvit_auth_token") || "";

      // customerId zusätzlich als Query mitsenden (FormData bleibt ebenfalls drin)
      const uploadUrl = `${getApiBaseUrl()}/api/admin/invoices/upload?customerId=${encodeURIComponent(
        customerId
      )}`;
      // Debug: if you still get 405, check in DevTools Network whether this URL is correct
      // and whether the 405 is actually for the OPTIONS preflight.
      // eslint-disable-next-line no-console
      console.log("[Invoices Upload] POST", uploadUrl);

      const res = await fetch(uploadUrl, {
        method: "POST",
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        body: fd,
      });

      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt || `Upload fehlgeschlagen (${res.status})`);
      }

      setUploadMsg("✅ Rechnung hochgeladen.");
      setMonth("");
      setAmount("");
      setStatus("Offen");
      setIssuedAt("");
      setPdfFile(null);

      await loadInvoices(customerId);
    } catch (e2) {
      setUploadMsg("");
      setError(e2?.message || "Upload fehlgeschlagen.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <MDBox mb={2} display="flex" justifyContent="space-between" alignItems="center">
        <MDTypography variant="h5" fontWeight="medium">
          Rechnungen (Admin)
        </MDTypography>
      </MDBox>

      <Grid container spacing={2}>
        <Grid item xs={12} md={5}>
          <Card>
            <MDBox p={2}>
              <MDTypography variant="h6" fontWeight="medium" mb={1}>
                Kunde auswählen
              </MDTypography>

              {loadingCustomers ? (
                <MDTypography variant="button" color="text">
                  Lädt…
                </MDTypography>
              ) : (
                <TextField
                  fullWidth
                  select
                  label="Kunde"
                  value={customerId}
                  onChange={(e) => setCustomerId(e.target.value)}
                >
                  <MenuItem value="">Bitte wählen</MenuItem>
                  {customers.map((c) => (
                    <MenuItem key={c.id} value={c.id}>
                      {c.customerNumber} – {c.companyName}
                    </MenuItem>
                  ))}
                </TextField>
              )}

              {error && (
                <MDTypography variant="button" color="error" mt={1}>
                  {error}
                </MDTypography>
              )}
            </MDBox>
          </Card>
        </Grid>

        <Grid item xs={12} md={7}>
          <Card>
            <MDBox p={2} component="form" onSubmit={onUpload}>
              <MDTypography variant="h6" fontWeight="medium" mb={1}>
                Rechnung hochladen
              </MDTypography>

              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Monat"
                    placeholder="2025-12"
                    value={month}
                    onChange={(e) => setMonth(e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Betrag (optional)"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    select
                    label="Status"
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                  >
                    <MenuItem value="Offen">Offen</MenuItem>
                    <MenuItem value="Bezahlt">Bezahlt</MenuItem>
                    <MenuItem value="Storniert">Storniert</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="date"
                    label="Ausgestellt am (optional)"
                    value={issuedAt}
                    onChange={(e) => setIssuedAt(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <input
                    type="file"
                    accept="application/pdf"
                    onChange={(e) => setPdfFile(e.target.files?.[0] || null)}
                  />
                </Grid>

                <Grid item xs={12}>
                  <MDButton type="submit" variant="gradient" color="info" disabled={uploading}>
                    {uploading ? "Hochladen…" : "Rechnung hochladen"}
                  </MDButton>

                  {uploadMsg && (
                    <MDTypography variant="button" color="success" ml={2}>
                      {uploadMsg}
                    </MDTypography>
                  )}
                </Grid>
              </Grid>
            </MDBox>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card>
            <MDBox p={2}>
              <MDTypography variant="h6" fontWeight="medium" mb={1}>
                Rechnungen
              </MDTypography>

              {loadingInvoices ? (
                <MDTypography variant="button" color="text">
                  Lädt…
                </MDTypography>
              ) : invoices.length === 0 ? (
                <MDTypography variant="button" color="text">
                  Keine Rechnungen vorhanden.
                </MDTypography>
              ) : (
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Monat</TableCell>
                        <TableCell>Betrag</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Ausgestellt</TableCell>
                        <TableCell>Erstellt</TableCell>
                        <TableCell>PDF</TableCell>
                        <TableCell align="right">Aktionen</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {invoices.map((inv) => (
                        <TableRow key={inv.id}>
                          <TableCell>{inv.month || "-"}</TableCell>
                          <TableCell>{inv.amount || "-"}</TableCell>
                          <TableCell>{inv.status || "-"}</TableCell>
                          <TableCell>{toDeDateTime(inv.issuedAt)}</TableCell>
                          <TableCell>{toDeDateTime(inv.createdAt)}</TableCell>
                          <TableCell>
                            {inv.pdfUrl ? (
                              <MDButton
                                variant="outlined"
                                size="small"
                                component="a"
                                href={buildDownloadUrl(inv.pdfUrl)}
                                target="_blank"
                                rel="noreferrer"
                              >
                                Download
                              </MDButton>
                            ) : (
                              "-"
                            )}
                          </TableCell>
                          <TableCell align="right">
                            <Stack direction="row" spacing={1} justifyContent="flex-end">
                              <IconButton aria-label="bearbeiten" size="small" onClick={() => openEdit(inv)}>
                                <Icon fontSize="small">edit</Icon>
                              </IconButton>
                              <IconButton
                                aria-label="löschen"
                                size="small"
                                onClick={() => deleteInvoice(inv)}
                                disabled={deletingId === inv.id}
                              >
                                <Icon fontSize="small">delete</Icon>
                              </IconButton>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </MDBox>
          </Card>
        </Grid>
      </Grid>

      <Dialog open={editOpen} onClose={closeEdit} maxWidth="sm" fullWidth>
        <DialogTitle>Rechnung bearbeiten</DialogTitle>
        <DialogContent>
          <MDBox mt={1}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Monat"
                  placeholder="2025-12"
                  value={editMonth}
                  onChange={(e) => setEditMonth(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Betrag (optional)"
                  value={editAmount}
                  onChange={(e) => setEditAmount(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  select
                  label="Status"
                  value={editStatus}
                  onChange={(e) => setEditStatus(e.target.value)}
                >
                  <MenuItem value="Offen">Offen</MenuItem>
                  <MenuItem value="Bezahlt">Bezahlt</MenuItem>
                  <MenuItem value="Storniert">Storniert</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="date"
                  label="Ausgestellt am (optional)"
                  value={editIssuedAt}
                  onChange={(e) => setEditIssuedAt(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12}>
                <MDTypography variant="caption" color="text">
                  PDF ändern ist aktuell nicht vorgesehen. (Falls du das willst, bauen wir einen separaten Re-Upload.)
                </MDTypography>
              </Grid>
            </Grid>
          </MDBox>
        </DialogContent>
        <DialogActions>
          <MDButton variant="text" color="dark" onClick={closeEdit} disabled={savingEdit}>
            Abbrechen
          </MDButton>
          <MDButton variant="gradient" color="info" onClick={saveEdit} disabled={savingEdit}>
            {savingEdit ? "Speichert…" : "Speichern"}
          </MDButton>
        </DialogActions>
      </Dialog>

      <Footer />
    </DashboardLayout>
  );
}
